import React, { useState, useEffect, useMemo, useRef } from 'react';
import html2canvas from 'html2canvas';
import { POSITIONS, VS_POSITIONS, RANKS, getHandName, ACTION_COLORS, getContrastColor } from '../api/rangesApi';
import '../styles/CheatSheet.css';

const CheatSheet = ({ ranges, onLoadRange }) => {
  const [myPosition, setMyPosition] = useState('BTN');
  const [vsPosition, setVsPosition] = useState('RFI');
  const [effectiveStack, setEffectiveStack] = useState(20);
  const [selectedRange, setSelectedRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null); // 'success' | 'error' | null
  const captureRef = useRef(null);
  const statusTimeoutRef = useRef(null);

  // Поиск подходящего диапазона
  const matchingRanges = useMemo(() => {
    return ranges.filter(range => {
      const posMatch = range.position === myPosition;
      
      // Проверяем vs_positions (массив):
      // - Пустой массив = подходит для всех позиций
      // - Иначе выбранная позиция должна быть в массиве
      const vsPositions = range.vs_positions || [];
      const vsMatch = vsPositions.length === 0 || vsPositions.includes(vsPosition);
      
      const stackMatch = effectiveStack >= (range.stack_from || 0) && 
                         effectiveStack <= (range.stack_to || 999);
      return posMatch && vsMatch && stackMatch;
    });
  }, [ranges, myPosition, vsPosition, effectiveStack]);

  // Автовыбор лучшего подходящего диапазона при изменении фильтров
  useEffect(() => {
    if (matchingRanges.length > 0) {
      // Приоритет: диапазон с точным совпадением vs_position в массиве
      const exactMatch = matchingRanges.find(r => 
        r.vs_positions && r.vs_positions.includes(vsPosition)
      );
      const bestMatch = exactMatch || matchingRanges[0];
      
      // Загружаем если это другой диапазон
      if (!selectedRange || selectedRange.id !== bestMatch.id) {
        handleSelectRange(bestMatch.id);
      }
    } else {
      setSelectedRange(null);
    }
  }, [matchingRanges, vsPosition]);

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectRange = async (id) => {
    setLoading(true);
    try {
      const range = await onLoadRange(id);
      // Применяем фиксированные цвета
      const actionsWithColors = range.actions.map(action => ({
        ...action,
        color: ACTION_COLORS[action.id] || action.color
      }));
      setSelectedRange({ ...range, actions: actionsWithColors });
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      setSelectedRange(null);
    } finally {
      setLoading(false);
    }
  };

  const showCopyStatus = (status) => {
    setCopyStatus(status);
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    statusTimeoutRef.current = setTimeout(() => {
      setCopyStatus(null);
    }, 2000);
  };

  const handleCopyRange = async () => {
    if (!captureRef.current || isCopying) return;
    setIsCopying(true);
    setCopyStatus(null);

    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: '#0f1115',
        scale: 2,
        useCORS: true,
        ignoreElements: (element) => {
          return element.classList?.contains('no-capture');
        }
      });

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (!blob) {
        throw new Error('Не удалось создать изображение');
      }

      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);

      showCopyStatus('success');
    } catch (error) {
      showCopyStatus('error');
    } finally {
      setIsCopying(false);
    }
  };

  // Получить стиль ячейки
  const getCellStyle = (hand) => {
    if (!selectedRange) return { background: '#2d3436', color: '#ffffff' };
    
    const handData = selectedRange.grid[hand] || {};
    const foldPercent = handData.fold ?? 100;
    
    if (foldPercent === 100) {
      return { background: '#2d3436', color: '#ffffff' };
    }
    
    // Находим доминирующий цвет
    let dominantColor = '#2d3436';
    let maxPercent = 0;
    
    const gradientParts = [];
    let currentPercent = 0;
    
    selectedRange.actions.forEach(action => {
      const actionPercent = handData[action.id] || 0;
      if (actionPercent > 0) {
        gradientParts.push(`${action.color} ${currentPercent}%`);
        currentPercent += actionPercent;
        gradientParts.push(`${action.color} ${currentPercent}%`);
        
        if (actionPercent > maxPercent) {
          maxPercent = actionPercent;
          dominantColor = action.color;
        }
      }
    });
    
    const textColor = getContrastColor(dominantColor);
    
    if (gradientParts.length <= 2) {
      return { 
        background: gradientParts[0]?.split(' ')[0] || '#2d3436',
        color: textColor
      };
    }
    
    return {
      background: `linear-gradient(135deg, ${gradientParts.join(', ')})`,
      color: textColor
    };
  };

  // Рассчёт Total Range
  const totalRange = useMemo(() => {
    if (!selectedRange) return '0.0';
    
    let total = 0;
    Object.entries(selectedRange.grid).forEach(([hand, data]) => {
      const weight = hand.length === 2 ? 6 : (hand.endsWith('s') ? 4 : 12);
      const foldPercent = data?.fold ?? 100;
      const playPercent = 100 - foldPercent;
      total += (playPercent / 100) * weight;
    });
    
    return ((total / 1326) * 100).toFixed(1);
  }, [selectedRange]);

  return (
    <div className="cheat-sheet">
      <div className="cs-header">
        <h1>🎯 Шпаргалка</h1>
        <p>Выбери параметры и получи диапазоны</p>
      </div>

      <div className="cs-controls">
        <div className="cs-control-group">
          <label>Моя позиция</label>
          <div className="cs-position-buttons">
            {POSITIONS.map(pos => (
              <button
                key={pos}
                className={`cs-pos-btn ${myPosition === pos ? 'active' : ''}`}
                onClick={() => setMyPosition(pos)}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>

        <div className="cs-control-group">
          <label>Против позиции</label>
          <div className="cs-position-buttons">
            {VS_POSITIONS.map(pos => (
              <button
                key={pos}
                className={`cs-pos-btn vs ${vsPosition === pos ? 'active' : ''} ${pos === 'RFI' || pos === 'MUX' ? 'special' : ''}`}
                onClick={() => setVsPosition(pos)}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>

        <div className="cs-control-group stack-control">
          <label>Эффективный стек</label>
          <div className="cs-stack-input">
            <input
              type="range"
              min="1"
              max="100"
              value={effectiveStack}
              onChange={(e) => setEffectiveStack(parseInt(e.target.value))}
            />
            <div className="cs-stack-value">
              <input
                type="number"
                value={effectiveStack}
                onChange={(e) => setEffectiveStack(Math.min(100, parseInt(e.target.value) || 0))}
                min="1"
                max="100"
              />
              <span>BB</span>
            </div>
          </div>
          <div className="cs-stack-presets">
            {[2, 3, 5, 7, 10, 13, 15, 20, 30, 40, 60, 80, 100].map(val => (
              <button
                key={val}
                className={`cs-stack-preset ${effectiveStack === val ? 'active' : ''}`}
                onClick={() => setEffectiveStack(val)}
              >
                {val}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Список подходящих диапазонов */}
      {matchingRanges.length > 1 && (
        <div className="cs-matching-ranges">
          <label>Найдено диапазонов: {matchingRanges.length}</label>
          <div className="cs-range-chips">
            {matchingRanges.map(range => (
              <button
                key={range.id}
                className={`cs-range-chip ${selectedRange?.id === range.id ? 'active' : ''}`}
                onClick={() => handleSelectRange(range.id)}
              >
                {range.name}
                <span className="chip-stack">{range.stack_from}-{range.stack_to}BB</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Диапазон */}
      <div className={`cs-range-display ${loading ? 'loading' : ''}`} ref={captureRef}>
        {matchingRanges.length === 0 ? (
          <div className="cs-no-range">
            <span className="cs-no-range-icon">🔍</span>
            <p>Диапазон не найден</p>
            <span>Попробуйте изменить параметры</span>
          </div>
        ) : selectedRange ? (
          <>
            <div className="cs-range-info">
              <h2>{selectedRange.name}</h2>
              <div className="cs-range-tags">
                <span className="cs-tag position">{selectedRange.position}</span>
                {selectedRange.vs_positions && selectedRange.vs_positions.length > 0 && (
                  <span className="cs-tag vs">
                    vs {selectedRange.vs_positions.length <= 3 
                      ? selectedRange.vs_positions.join(', ')
                      : `${selectedRange.vs_positions.slice(0, 2).join(', ')}+${selectedRange.vs_positions.length - 2}`}
                  </span>
                )}
                <span className="cs-tag stack">
                  {selectedRange.stack_from}-{selectedRange.stack_to} BB
                </span>
                <span className="cs-tag total">Range: {totalRange}%</span>
                <button
                  type="button"
                  className="cs-tag cs-copy-tag no-capture"
                  onClick={handleCopyRange}
                  disabled={isCopying}
                >
                  {isCopying ? 'Копирование...' : 'Скопировать'}
                </button>
                {copyStatus === 'success' && (
                  <span className="cs-copy-status success no-capture">Скопировано</span>
                )}
                {copyStatus === 'error' && (
                  <span className="cs-copy-status error no-capture">Ошибка копирования</span>
                )}
              </div>
              {selectedRange.comment && (
                <div className="cs-comment">
                  {selectedRange.comment.split('\n').map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="cs-grid">
              {RANKS.map((_, rowIndex) => (
                <div key={rowIndex} className="cs-grid-row">
                  {RANKS.map((_, colIndex) => {
                    const hand = getHandName(rowIndex, colIndex);
                    const isPair = rowIndex === colIndex;
                    
                    return (
                      <div
                        key={hand}
                        className={`cs-cell ${isPair ? 'pair' : ''}`}
                        style={getCellStyle(hand)}
                      >
                        <span>{hand}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="cs-legend">
              {selectedRange.actions.filter(a => a.id !== 'fold').map(action => (
                <div key={action.id} className="cs-legend-item">
                  <span className="cs-legend-color" style={{ backgroundColor: action.color }} />
                  <span>{action.name}</span>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CheatSheet;

